import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { createPageUrl } from "@/shared/utils";
import { auth } from "@/shared/api/graphqlClient";
import * as merchantService from "@/shared/api/services/merchantService";
import {
  LanguageProvider,
  useLanguage,
} from "@/shared/components/shared/LanguageContext";
import LanguageSwitcher from "@/shared/components/shared/LanguageSwitcher";
import {
  LayoutDashboard,
  ArrowDownToLine,
  ArrowUpFromLine,
  Receipt,
  Settings,
  LogOut,
  Menu,
  X,
  Store,
  Bell,
  User,
  ChevronLeft,
  ChevronRight,
  Server,
  Route,
  Webhook,
  FileText,
} from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import NotificationDropdown from "@/shared/components/shared/NotificationDropdown";

const getNavItems = (t) => [
  { name: t("dashboard"), icon: LayoutDashboard, page: "MerchantDashboard" },
  { name: t("transactions"), icon: Receipt, page: "MerchantTransactions" },
  { name: t("deposits"), icon: ArrowDownToLine, page: "MerchantDeposit" },
  {
    name: t("withdrawals"),
    icon: ArrowUpFromLine,
    page: "MerchantWithdrawals",
  },
  { name: t("notifications"), icon: Bell, page: "MerchantNotifications" },
  {
    name: t("providers"),
    icon: Server,
    page: "MerchantProviders",
    viewOnly: true,
  },
  { name: t("routing"), icon: Route, page: "MerchantRouting" },
  { name: t("webhooks"), icon: Webhook, page: "MerchantWebhooks" },
  { name: t("settlements"), icon: FileText, page: "MerchantSettlements" },
  { name: t("settings"), icon: Settings, page: "MerchantSettings" },
];

function MerchantLayoutContent({ children, currentPage }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [merchantName, setMerchantName] = useState("");
  const [merchantCode, setMerchantCode] = useState("");
  const [currency, setCurrency] = useState("");
  const navigate = useNavigate();
  const { t } = useLanguage();
  const navItems = getNavItems(t);

  useEffect(() => {
    merchantService
      .getProfile()
      .then((profile) => {
        if (profile?.name) setMerchantName(profile.name);
        if (profile?.code) setMerchantCode(profile.code);
        if (profile?.currency) setCurrency(profile.currency);
      })
      .catch(() => { });
  }, []);

  const handleLogout = () => {
    auth.logout();
    window.location.href = "/MerchantLogin";
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <style>{`
        .merchant-sidebar {
          background: linear-gradient(180deg, #1e3a5f 0%, #0f2744 100%);
        }
        .merchant-nav-item {
          transition: all 0.2s ease;
        }
        .merchant-nav-item:hover {
          transform: translateX(4px);
        }
        .merchant-nav-item.active {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
        }
        .merchant-sidebar::-webkit-scrollbar,
        .merchant-sidebar nav::-webkit-scrollbar {
          width: 6px;
        }
        .merchant-sidebar::-webkit-scrollbar-track,
        .merchant-sidebar nav::-webkit-scrollbar-track {
          background: transparent;
        }
        .merchant-sidebar::-webkit-scrollbar-thumb,
        .merchant-sidebar nav::-webkit-scrollbar-thumb {
          background: rgba(147, 197, 253, 0.3);
          border-radius: 3px;
        }
        .merchant-sidebar::-webkit-scrollbar-thumb:hover,
        .merchant-sidebar nav::-webkit-scrollbar-thumb:hover {
          background: rgba(147, 197, 253, 0.5);
        }
      `}</style>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-slate-900 z-50 flex items-center justify-between px-3 sm:px-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="text-white"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Menu className="h-5 w-5" />
            )}
          </Button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
              <Store className="h-4 w-4 text-white" />
            </div>
            <span className="font-bold text-sm sm:text-base text-white truncate max-w-[42vw]">
              Luxkgate
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          <LanguageSwitcher
            variant="ghost"
            className="h-9 w-auto px-2 text-white"
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="relative h-9 w-auto px-3 text-white gap-2"
              >
                <User className="h-5 w-5 shrink-0" />
                <div className="flex flex-col items-start leading-tight max-w-[42vw] sm:max-w-[48vw]">
                  <span className="text-[11px] sm:text-sm font-semibold truncate hidden md:block">
                    {merchantName || t("merchant")}
                  </span>
                  <span className="text-[10px] sm:text-xs text-slate-300 truncate hidden md:block">
                    {merchantCode || t("merchant")}
                  </span>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-3 py-2 border-b">
                <p className="text-sm font-medium">
                  {merchantName || t("merchant")}
                </p>
                <p className="text-xs text-slate-500">
                  {merchantCode || t("merchant")}
                </p>
              </div>
              <DropdownMenuItem
                onClick={() => navigate(createPageUrl("MerchantSettings"))}
              >
                <Settings className="h-4 w-4 mr-2" />
                {t("settings")}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-red-600" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" />
                {t("logout")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <NotificationDropdown accentColor="blue" />
        </div>
      </div>

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`merchant-sidebar fixed top-0 left-0 h-screen z-50 transition-all duration-300 ease-in-out flex flex-col
          ${collapsed ? "w-[84vw] max-w-72 lg:w-20" : "w-[84vw] max-w-72 lg:w-64"}
          ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
        `}
      >
        {/* Logo */}
        <div
          className={`h-16 flex items-center border-b border-slate-700/50 flex-shrink-0 ${collapsed ? "justify-center px-2" : "px-6"}`}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center shadow-lg shadow-blue-500/30">
              <Store className="h-5 w-5 text-white" />
            </div>
            {!collapsed && (
              <div>
                <h1 className="font-bold text-white text-lg tracking-tight">
                  Luxkgate
                </h1>
                <p className="text-[10px] text-blue-300 uppercase tracking-wider">
                  {t("merchantPortal")}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1 flex-1 overflow-y-auto">
          {!collapsed && (
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-3 px-3">
              {t("navigation")}
            </p>
          )}

          {navItems.map((item) => {
            const isActive = currentPage === item.page;
            return (
              <Link
                key={item.page}
                to={createPageUrl(item.page)}
                onClick={() => setMobileOpen(false)}
                className={`merchant-nav-item flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
                  ${isActive
                    ? "active text-white"
                    : "text-slate-300 hover:text-white hover:bg-slate-700/50"
                  }
                  ${collapsed ? "justify-center" : ""}
                `}
              >
                <item.icon
                  className={`h-5 w-5 flex-shrink-0 ${isActive ? "text-white" : ""}`}
                />
                {!collapsed && (
                  <div className="flex items-center justify-between flex-1">
                    <span>{item.name}</span>
                    {item.viewOnly && (
                      <span className="text-[10px] bg-blue-500/50 px-1.5 py-0.5 rounded">
                        View
                      </span>
                    )}
                  </div>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Merchant Info */}
        {!collapsed && (
          <div className="mt-auto px-4 pb-16">
            <div className="bg-gradient-to-r from-blue-600/20 to-cyan-600/20 rounded-xl p-4 border border-blue-500/30">
              <p className="text-xs text-blue-300 mb-1">{t("loggedInAs")}</p>
              <p className="text-white font-semibold text-sm truncate">
                {merchantName}
              </p>
              {merchantCode && (
                <p className="text-xs text-blue-300/80 font-mono mt-1">
                  {merchantCode}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Collapse Button */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="hidden lg:flex absolute bottom-4 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-slate-700 items-center justify-center text-slate-400 hover:text-white hover:bg-slate-600 transition-colors"
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </button>
      </aside>

      {/* Main Content */}
      <div
        className={`transition-all duration-300 ${collapsed ? "lg:pl-20" : "lg:pl-64"}`}
      >
        {/* Top Bar */}
        <header className="sticky top-0 z-30 hidden  md:flex h-16 items-center justify-between px-6 bg-white/80 backdrop-blur-lg border-b border-slate-200">
          <div className="flex-1" />
          <div className="flex items-center gap-3">
            <LanguageSwitcher variant="ghost" />
            <NotificationDropdown accentColor="blue" />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="flex items-center gap-2 pl-2 pr-3"
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                    <User className="h-4 w-4 text-white" />
                  </div>
                  <div className="text-left hidden sm:block">
                    <p className="text-sm font-medium">{merchantName}</p>
                    <div className="flex items-center gap-2">
                      <p className="text-xs text-slate-500">{t("merchant")}</p>
                      {currency && (
                        <span className="text-[10px] font-bold bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded uppercase border border-slate-200">
                          {currency}
                        </span>
                      )}
                    </div>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2 border-b">
                  <p className="text-sm font-medium">{merchantName}</p>
                  <p className="text-xs text-slate-500">
                    {merchantCode || t("merchant")}
                  </p>
                </div>
                <DropdownMenuItem
                  onClick={() => navigate(createPageUrl("MerchantSettings"))}
                >
                  <Settings className="h-4 w-4 mr-2" />
                  {t("settings")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-red-600"
                  onClick={handleLogout}
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  {t("logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-3 sm:p-4 lg:p-6 pt-20 lg:pt-6 min-h-[calc(100vh-4rem)] overflow-x-hidden">
          {children}
        </main>
      </div>
    </div>
  );
}

export default function MerchantLayout(props) {
  return <MerchantLayoutContent {...props} />;
}
